import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, query, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import "./Home.css";

function Home() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [highlightedEvents, setHighlightedEvents] = useState([]);
  const [randomClasses, setRandomClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [siteSettings, setSiteSettings] = useState({
    showEvents: true,
    showClasses: true,
  });

  useEffect(() => {
    fetchData();
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const settingsRef = doc(db, "siteSettings", "global");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        setSiteSettings(settingsSnap.data());
      }
    } catch (err) {
      console.error("Error fetching site settings:", err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const today = new Date().toISOString().split("T")[0];

      // Fetch events only if events are enabled
      if (siteSettings.showEvents) {
        const eventsRef = collection(db, "events");
        const eventsQuery = query(eventsRef);
        const eventsSnapshot = await getDocs(eventsQuery);

        const allEvents = [];
        eventsSnapshot.forEach((doc) => {
          allEvents.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Filter today's events
        const todayEvs = allEvents.filter((event) => event.date === today);
        setTodayEvents(todayEvs);

        // Filter upcoming events (excluding today)
        const upcomingEvs = allEvents
          .filter((event) => event.date > today)
          .slice(0, 3);
        setUpcomingEvents(upcomingEvs);

        // Filter highlighted events
        const highlightedEvs = allEvents
          .filter((event) => event.highlighted && event.date >= today)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        setHighlightedEvents(highlightedEvs);
      } else {
        // Clear events if section is disabled
        setTodayEvents([]);
        setUpcomingEvents([]);
        setHighlightedEvents([]);
      }

      // Fetch classes only if classes are enabled
      if (siteSettings.showClasses) {
        const classesRef = collection(db, "classes");
        const classesSnapshot = await getDocs(classesRef);

        const allClasses = [];
        classesSnapshot.forEach((doc) => {
          allClasses.push({
            id: doc.id,
            ...doc.data(),
          });
        });

        // Select 3 random classes
        const shuffled = [...allClasses].sort(() => 0.5 - Math.random());
        const selectedClasses = shuffled.slice(0, 3);
        setRandomClasses(selectedClasses);
      } else {
        // Clear classes if section is disabled
        setRandomClasses([]);
      }

      setLoading(false);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load content");
      setLoading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { weekday: "short", month: "short", day: "numeric" };
    return date.toLocaleDateString("es-AR", options);
  };

  // Format full date for highlighted event
  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    };
    return date.toLocaleDateString("es-AR", options);
  };

  // Truncate description for preview
  const truncateDescription = (description, maxLength = 80) => {
    if (!description) return "";
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength) + "...";
  };

  // Don't show sections if they're disabled in settings
  const shouldShowEvents =
    siteSettings.showEvents &&
    (highlightedEvents.length > 0 ||
      upcomingEvents.length > 0 ||
      todayEvents.length > 0);
  const shouldShowClasses =
    siteSettings.showClasses && randomClasses.length > 0;

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>Espacio Paradigmas</h1>
          <h2>Centro cultural</h2>
          <p className="tagline">
            Un espacio para conectar con la creatividad...
          </p>
          <p className="description">
            Una nueva propuesta para desarrollar la creatividad y explorar los
            sentidos, conectar comunidades y crear memorias.
          </p>
        </div>
      </section>

      {/* Highlighted Events Section - Only show if events are enabled */}
      {siteSettings.showEvents && highlightedEvents.length > 0 && (
        <section className="highlighted-events-section">
          <div className="container">
            <div className="section-header">
              <h3>EVENTO DESTACADO</h3>
              <div className="highlighted-badge">⭐ DESTACADO</div>
            </div>

            <div className="highlighted-events-grid">
              {highlightedEvents.slice(0, 1).map((event) => (
                <div key={event.id} className="highlighted-event-card">
                  <div className="highlighted-event-content">
                    {/* Event Image */}
                    {event.imgLink && (
                      <div className="highlighted-event-image">
                        <img
                          src={event.imgLink}
                          alt={event.title}
                          className="highlighted-image"
                        />
                        <div className="image-overlay"></div>
                      </div>
                    )}

                    {/* Event Info */}
                    <div
                      className={`highlighted-event-info ${event.imgLink ? "with-image" : ""}`}
                    >
                      <div className="highlighted-event-header">
                        <h2>{event.title}</h2>
                        <div className="highlighted-event-date">
                          <span className="highlighted-date-full">
                            {formatFullDate(event.date)}
                          </span>
                          <span className="highlighted-date-time">
                            • {event.time}
                          </span>
                        </div>
                      </div>

                      {event.description && (
                        <div className="highlighted-event-description">
                          <p>{event.description}</p>
                        </div>
                      )}

                      {/* Event Details */}
                      {(event.cost || event.duration || event.website) && (
                        <div className="highlighted-event-details">
                          {event.duration && (
                            <div className="highlighted-detail-item">
                              <span className="highlighted-detail-label">
                                Duración:
                              </span>
                              <span className="highlighted-detail-value">
                                {event.duration}
                              </span>
                            </div>
                          )}

                          {event.cost && (
                            <div className="highlighted-detail-item">
                              <span className="highlighted-detail-label">
                                Costo:
                              </span>
                              <span className="highlighted-detail-value">
                                {event.cost}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="highlighted-event-actions">
                        {event.website ? (
                          <a
                            href={event.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="highlighted-ticket-btn"
                          >
                            Comprar entrada
                          </a>
                        ) : (
                          <button className="highlighted-info-btn">
                            Más información
                          </button>
                        )}
                        <Link
                          to={`/agenda?event=${event.id}`}
                          className="highlighted-view-btn"
                        >
                          Ver en agenda
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Show if there are more than 1 highlighted events */}
            {highlightedEvents.length > 1 && (
              <div className="more-highlighted">
                <p className="more-highlighted-text">
                  +{highlightedEvents.length - 1} evento(s) destacado(s) más
                </p>
                <Link to="/agenda" className="view-all-highlighted">
                  Ver todos los eventos →
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Upcoming Events Preview - Only show if events are enabled */}
      {siteSettings.showEvents && (
        <section className="events-preview">
          <div className="container">
            <div className="section-header">
              <h3>PRÓXIMOS EVENTOS</h3>
              {siteSettings.showEvents && (
                <Link to="/agenda" className="view-all">
                  Ver agenda completa →
                </Link>
              )}
            </div>

            {error ? (
              <div className="error-state">
                <p>{error}</p>
                <button onClick={fetchData} className="retry-btn">
                  Prueba de nuevo
                </button>
              </div>
            ) : loading ? (
              <div className="loading-events">
                <p>Cargando contenido...</p>
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="no-events">
                <p>No hay eventos próximamente...</p>
              </div>
            ) : (
              <>
                <div className="events-grid">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className="event-card">
                      <div className="event-date">
                        <span className="date-day">
                          {new Date(event.date).getDate()}
                        </span>
                        <span className="date-month">
                          {new Date(event.date).toLocaleDateString("es-AR", {
                            month: "short",
                          })}
                        </span>
                      </div>
                      <div className="event-details">
                        <h4>{event.title}</h4>
                        <p className="event-time">
                          {formatDate(event.date)} • {event.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {siteSettings.showEvents && (
                  <div className="center-button">
                    <Link to="/agenda" className="agenda-button">
                      Ver todos los eventos
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Random Classes Preview - Only show if classes are enabled */}
      {siteSettings.showClasses && randomClasses.length > 0 && (
        <section className="classes-preview-section">
          <div className="container">
            <div className="section-header">
              <h3>CLASES DESTACADAS</h3>
              {siteSettings.showClasses && (
                <Link to="/classes" className="view-all">
                  Ver todas las clases →
                </Link>
              )}
            </div>

            <div className="classes-grid">
              {randomClasses.map((classItem) => {
                // Determine class type for background - SAME LOGIC AS Classes.js
                const getImageClass = (cls) => {
                  if (cls.image && cls.image.trim() !== "") {
                    return cls.image.toLowerCase();
                  }

                  // Default fallback classes based on common class types
                  const title = cls.title ? cls.title.toLowerCase() : "";
                  if (
                    title.includes("danza") ||
                    title.includes("baile") ||
                    title.includes("dance")
                  ) {
                    return "dance";
                  }
                  if (title.includes("pintura") || title.includes("paint")) {
                    return "painting";
                  }
                  if (title.includes("yoga") || title.includes("meditación")) {
                    return "yoga";
                  }
                  if (title.includes("foto") || title.includes("photo")) {
                    return "photography";
                  }
                  if (
                    title.includes("escritura") ||
                    title.includes("writing")
                  ) {
                    return "writing";
                  }
                  if (title.includes("cerámica") || title.includes("ceramic")) {
                    return "ceramics";
                  }

                  // Default fallback
                  return "default";
                };

                const imageClass = getImageClass(classItem);
                const hasBackgroundImage =
                  classItem.backgroundImage &&
                  classItem.backgroundImage.trim() !== "";

                return (
                  <Link
                    key={classItem.id}
                    to={`/classes/${classItem.id}`}
                    className={`class-card ${imageClass}`}
                    style={
                      hasBackgroundImage
                        ? {
                            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.7)), url('${classItem.backgroundImage}')`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }
                        : {}
                    }
                  >
                    <div className="class-overlay">
                      <div className="class-content">
                        <h3>{classItem.title || "Clase"}</h3>
                        {classItem.schedule && (
                          <p className="class-schedule">{classItem.schedule}</p>
                        )}
                        {classItem.description && (
                          <p className="class-description">
                            {truncateDescription(classItem.description, 100)}
                          </p>
                        )}
                        <div className="class-footer">
                          <div className="class-meta">
                            {classItem.level && (
                              <span className="class-level">
                                {classItem.level}
                              </span>
                            )}
                            {classItem.price && (
                              <span className="class-price">
                                {classItem.price}
                              </span>
                            )}
                            {classItem.instructor && (
                              <span className="instructor">
                                {classItem.instructor}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {siteSettings.showClasses && (
              <div className="center-button">
                <Link to="/classes" className="view-classes-btn">
                  Explorar todas las clases
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Today's Events (Optional) - Only show if events are enabled */}
      {siteSettings.showEvents && todayEvents.length > 0 && (
        <section className="today-events-section">
          <div className="container">
            <h3>Hoy en Paradigmas</h3>
            <div className="today-events-grid">
              {todayEvents.map((event) => (
                <div key={event.id} className="today-event-card">
                  <h4>{event.title}</h4>
                  <p>{event.time}</p>
                  {event.description && (
                    <p className="event-desc">{event.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Info Section - Always show */}
      <section className="info-section">
        <div className="container">
          <div className="info-grid">
            <div className="info-item">
              <div className="info-icon">🖼️</div>
              <h4>Muestras</h4>
              <p>Muestras de arte y disciplinas.</p>
            </div>
            <div className="info-item">
              <div className="info-icon">🎵</div>
              <h4>Shows</h4>
              <p>Música & performance</p>
            </div>
            <div className="info-item">
              <div className="info-icon">📚</div>
              <h4>Charlas y lecturas</h4>
              <p>Seminarios, charlas, debates, clases abiertas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Optional: Show message when both sections are disabled */}
      {!siteSettings.showEvents && !siteSettings.showClasses && !loading && (
        <section className="no-content-message">
          <div className="container">
            <div className="no-content-card">
              <p className="no-content-title">Sitio en mantenimiento</p>
              <p className="no-content-text">
                Las secciones de eventos y clases están temporalmente
                deshabilitadas. Pronto habrá nuevas actividades.
              </p>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default Home;
